import { Apollo, gql } from 'apollo-angular';
import { Injectable } from '@angular/core';
import { BaseRepository } from '../base.repository';
import { UserDto, UserPermissionsDto } from '../../dtos/user.dto';
import { UserMapper } from '../../mappers/user.mapper';
import { AuthenticationService } from 'src/app/auth/authentication-service/authentication.service';
import { User } from 'src/app/entities/user/user';
import { UserAuthenticationDataManagerService } from 'src/app/auth/user-authentication-data-manager-service/user-authentication-data-manager.service';
import { DateService } from 'src/app/services/date-service/date.service';
import { catchError, map } from 'rxjs';
import log from 'loglevel';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';


export enum Roles {
  chronos_tracker = "chronos_tracker",
  chronos_manager = "chronos_manager",
  administrator = "administrator",
  api = "api",
  anonymous = "anonymous",
  authenticated = "authenticated"
}


/**
 * GraphQL arguments for `get_time_tracks` query.
 */
export interface UserFilters {
  page?: number;
  limit?: number;
  roles?: Roles[];
  groups?: string[];
  name?: string;
}

/**
 * GraphQL query used by this gateway.
 *
 * Exported because is required by tests.
 */
@Injectable({
  providedIn: 'root',
})
export class UserRepository extends BaseRepository<
  User,
  UserDto
> {
  protected override _mapper = new UserMapper();
  protected override dataPropertyName = 'get_users';

  protected override logger = log.getLogger('UserRepository');

  constructor(
    protected override apollo: Apollo,
    protected override snackBar: SnackbarService,
    protected authService: AuthenticationService,
    protected userAuth: UserAuthenticationDataManagerService,
    protected dateService: DateService
  ) {
    super(apollo, snackBar);
  }

  /******************/
  /* Public methods */
  /******************/
  public override getGqlQuery() {
    return gql`
      query UsersQuery($page: Int, $limit: Int, $roles: [Roles], $groups: [ID], $name: String) {
        get_users (page: $page, limit: $limit, roles: $roles, groups: $groups, name: $name) {
          items {
            uuid
            name
            roles
            mail
            status
            groups {
              name
              uuid
            }
            permissions {
              update
              delete
            }
          }
          total
          pager {
            current
            next
            prev
          }
        }
      }
    `;
  }


  /**
   * Function to add new track
   * @param track
   * @returns A boolean observable that indicates if query was successful.
   */
  public getCurrentUser() {
    const GETCURRENTUSER = gql`
      query UsersQuery {
        get_current_user {
          user {
            name
            status
            mail
            roles
            uuid
            permissions {
              update
              delete
            }
          }
        }
      }
    `;

    return this.apollo
      .watchQuery({
        query: GETCURRENTUSER,
      })
      .valueChanges
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        }),
        map((user) => {
          const data = user.data as { get_current_user: { user: UserDto } }
          return this._mapper.mapToEntity(data.get_current_user.user)
        })
      );
  }


  public getUserPermissions() {
    const GETUSERPERMISSIONS = gql`
      query UsersQuery {
        get_current_user_permissions {
          permissions {
            create_time_tracks
            create_users
            create_clients
            create_projects
            view_time_tracks
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<{ get_current_user_permissions: { permissions: UserPermissionsDto } }>({
        query: GETUSERPERMISSIONS,
      })
      .valueChanges
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        }),
        map(response => response.data.get_current_user_permissions.permissions)
      );
  }

  /**
   * Deletes a user from the system
   * @param user The user to delete
   * @returns Observable with the deleted user's data
   */
  public deleteUser(user: User) {
    const DELETE_USER = gql`
      mutation DeleteUser($uuid: String!) {
        delete_user(uuid: $uuid) {
          user {
            uuid
            name
            mail
          }
          errors
        }
      }
    `;

    return this.apollo.mutate({
      mutation: DELETE_USER,
      variables: {
        uuid: user.id
      }
    }).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.snackBar.showError(error);
        return [];
      }),
      map((result) => {
        const data = result.data as { delete_user: { user: UserDto } };
        return this._mapper.mapToEntity(data.delete_user.user);
      })
    );
  }

  /**
   * Adds a new user to the system
   * @param user The user to add
   * @returns Observable with the new user's data
   */
  public addNewUser(user: User, pass?: string) {
    const ADDUSER = gql`
      mutation MyMutation($uuid: ID!, $name: String!, $mail: String!, $pass: String!, $status: Boolean!, $roles: [Roles!]!) {
        create_user( data: {uuid: $uuid, name: $name, mail: $mail, pass: $pass, status: $status, roles: $roles}) {
          user {
            uuid
            status
            name
            mail
            roles
            groups {
              name
              uuid
            }
          }
          errors
        }
      }
    `;

    return this.apollo.mutate({
      mutation: ADDUSER,
      variables: {
        uuid: user.id,
        name: user.name,
        mail: user.mail,
        pass: pass || "x!6B1r1<s30h", // TODO: Don't hardcode password
        status: user.status,
        roles: user.roles
      }
    }).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.snackBar.showError(error);
        return []
      }),
      map((result) => {
        const data = result.data as { create_user: UserDto };
        return data.create_user
      })
    );
  }

  /**
   * Updates an existing user in the system
   * @param user The user to update
   * @returns Observable with the updated user's data
   */
  public updateUser(user: User) {
    const UPDATEUSER = gql`
      mutation UpdateUser($uuid: ID!, $name: String!, $mail: String!, $roles: [Roles!]) {
        update_user(uuid: $uuid, data: {name: $name, mail: $mail, roles: $roles}) {
          user {
            uuid
            name
            mail
            status
            roles
            groups {
              name
              uuid
            }
          }
          errors
        }
      }
    `;

    return this.apollo.mutate({
      mutation: UPDATEUSER,
      variables: {
        uuid: user.id,
        name: user.name,
        mail: user.mail,
        roles: user.roles
      }
    }).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.snackBar.showError(error);
        return [];
      }),
      map((result) => {
        const data = result.data as { update_user: { user: UserDto } };
        return this._mapper.mapToEntity(data.update_user.user);
      })
    );
  }
}
